/**
 * Image-normalized (0–1) ↔ table UV.
 * u = 0 at the court-left end (Player A), 1 at court-right (Player B).
 * v = 0 at the image-top sideline, 1 at the image-bottom sideline.
 *
 * Keep in sync with Backend/src/utils/tableUv.js
 */

import { normalizeTableCorners } from "./tableCorners";

/** ITTF table (m). Used to convert UV deltas into a metric proxy. */
export const TABLE_LENGTH_M = 2.74;
export const TABLE_WIDTH_M = 1.525;

export function orderTableCorners(raw) {
  const pts = normalizeTableCorners(raw);
  if (!pts) return null;
  const sorted = [...pts].sort((a, b) => a.y - b.y || a.x - b.x);
  const top = sorted.slice(0, 2).sort((a, b) => a.x - b.x);
  const bottom = sorted.slice(2).sort((a, b) => a.x - b.x);
  return { tl: top[0], tr: top[1], bl: bottom[0], br: bottom[1] };
}

function solveLinear(A, b) {
  const n = b.length;
  const m = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let r = col + 1; r < n; r += 1) {
      if (Math.abs(m[r][col]) > Math.abs(m[pivot][col])) pivot = r;
    }
    if (Math.abs(m[pivot][col]) < 1e-12) return null;
    if (pivot !== col) {
      const tmp = m[col];
      m[col] = m[pivot];
      m[pivot] = tmp;
    }
    const div = m[col][col];
    for (let c = col; c <= n; c += 1) m[col][c] /= div;
    for (let r = 0; r < n; r += 1) {
      if (r === col) continue;
      const f = m[r][col];
      if (Math.abs(f) < 1e-15) continue;
      for (let c = col; c <= n; c += 1) m[r][c] -= f * m[col][c];
    }
  }
  return m.map((row) => row[n]);
}

/**
 * Homography mapping image (x,y) → table (u,v), h22 = 1.
 */
export function tableHomography(raw) {
  const q = orderTableCorners(raw);
  if (!q) return null;
  const src = [q.tl, q.tr, q.br, q.bl];
  const dst = [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: 0, y: 1 },
  ];
  const A = [];
  const b = [];
  for (let i = 0; i < 4; i += 1) {
    const { x, y } = src[i];
    const u = dst[i].x;
    const v = dst[i].y;
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    b.push(v);
  }
  const h = solveLinear(A, b);
  if (!h) return null;
  return h;
}

export function applyTableHomography(h, x, y) {
  if (!h || !Number.isFinite(x) || !Number.isFinite(y)) return null;
  const denom = h[6] * x + h[7] * y + 1;
  if (Math.abs(denom) < 1e-9) return null;
  const u = (h[0] * x + h[1] * y + h[2]) / denom;
  const v = (h[3] * x + h[4] * y + h[5]) / denom;
  if (!Number.isFinite(u) || !Number.isFinite(v)) return null;
  return { u, v };
}

export function imageToTableUV(x, y, raw, homography = null) {
  const h = homography || tableHomography(raw);
  return applyTableHomography(h, x, y);
}

/** Metric proxy on the table plane (hips are near, not on, that plane). */
export function tableUvDeltaM(du, dv) {
  const eu = Number(du);
  const ev = Number(dv);
  if (!Number.isFinite(eu) || !Number.isFinite(ev)) return null;
  return Math.hypot(eu * TABLE_LENGTH_M, ev * TABLE_WIDTH_M);
}

/** Positive = behind that player's end line (standing room). */
export function depthBehindEndU(u, player) {
  const uu = Number(u);
  if (!Number.isFinite(uu)) return null;
  return player === "B" ? uu - 1 : -uu;
}
